import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import FocusableContainer from '../FocusableContainer'
import Search from '../Search'
import { CaretDownIcon } from '../../../icons'
import { useDeepCompareEffect } from '../../../hooks/useDeepCompareEffect'
import { useOnClickOutside } from '../../../hooks/useOnClickOutside'
import { useLocalStorage } from '../../../hooks/useLocalStorage'
import { keyboardEvents } from '../../../utils/keyboardEvents'
import { COLTENE_BRANCH_UUID, COLTENE_BRANCHES } from '../../../actions/root'
import './switch-branch-dropdown.scss'

const SwitchBranchDropdown = (props) => {
  const { onAfterBranchChange, hasError, isDisabled, className = '' } = props
  const { t } = useTranslation()
  const [colteneBranchId, setColteneBranchId] = useLocalStorage(COLTENE_BRANCH_UUID)
  const colteneBranches = JSON.parse(localStorage.getItem(COLTENE_BRANCHES) || '[]')
  const [options, setOptions] = useState([])
  const [isSelectMenuOpen, setIsSelectMenuOpen] = useState(false)
  const [selectedKey, setSelectedKey] = useState(colteneBranchId)
  const [focusedOption, setFocusedOption] = useState(0)
  const [keyboardFocused, setKeyboardFocused] = useState(false)
  const [filteredOptions, setFilteredOptions] = useState([])
  const ref = useRef()
  const filteringRef = useRef()
  const optionsRefs = useRef([])

  useDeepCompareEffect(() => {
    if (colteneBranches.length === 0) {
      return
    }

    const mappedOptions = colteneBranches.map((branch) => ({
      key: branch.branchUuid,
      value: branch.branchName,
    }))

    setOptions(mappedOptions)
  }, [colteneBranches])

  useEffect(() => {
    mapOptionsToFiltered()
  }, [options])

  useEffect(() => {
    if (colteneBranchId !== selectedKey) {
      setSelectedKey(colteneBranchId)
    }
  }, [colteneBranchId])

  useEffect(() => {
    if (isSelectMenuOpen) {
      setFocusedOption(0)
    }

    if (!isSelectMenuOpen) {
      setKeyboardFocused(false)
    }
  }, [isSelectMenuOpen])

  useEffect(() => {
    if (focusedOption === 0 && filteringRef.current) {
      filteringRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })

      return
    }

    if (optionsRefs.current[focusedOption]) {
      optionsRefs.current[focusedOption].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [focusedOption])

  useOnClickOutside(ref, () => {
    setIsSelectMenuOpen(false)
  })

  const searchOption = (value) => {
    setFocusedOption(0)

    if (!value) {
      mapOptionsToFiltered()
      return
    }

    const filtered =
      options
        ?.filter((option) => option.value?.toLowerCase()?.includes(value.toLowerCase()))
        .map((opt) => ({ key: opt.key, value: opt.value, isDisabled: opt.isDisabled })) || []
    setFilteredOptions(filtered)
  }

  const handleOptionSelection = (option) => {
    if (option.isDisabled) {
      return
    }

    setSelectedKey(option.key)
    setIsSelectMenuOpen(false)

    if (option.key !== selectedKey) {
      setColteneBranchId(option.key, false)
      onAfterBranchChange()
      window.location.replace('/')
    }

    mapOptionsToFiltered()
  }

  const handleKeyDown = (event) => {
    if (isDisabled) {
      return
    }

    if (event.key === keyboardEvents.ENTER) {
      setKeyboardFocused(true)

      if (!isSelectMenuOpen) {
        setIsSelectMenuOpen(true)
        return
      }

      const option = filteredOptions[focusedOption]
      if (!option.isDisabled) {
        handleOptionSelection(option)
      }
    }

    if (event.key === keyboardEvents.ESC) {
      setIsSelectMenuOpen(false)
    }

    if (event.key === keyboardEvents.ARROW_DOWN) {
      event.stopPropagation()
      event.preventDefault()
      setKeyboardFocused(true)

      setFocusedOption((prev) => {
        return prev < filteredOptions.length - 1 ? prev + 1 : 0
      })
    }

    if (event.key === keyboardEvents.ARROW_UP) {
      event.stopPropagation()
      event.preventDefault()
      setKeyboardFocused(true)

      setFocusedOption((prev) => {
        return prev > 0 ? prev - 1 : filteredOptions.length - 1
      })
    }
  }

  const mapOptionsToFiltered = () => {
    setFilteredOptions(options?.map((opt) => ({ key: opt.key, value: opt.value, isDisabled: opt.isDisabled })) || [])
  }

  return (
    <FocusableContainer
      ref={ref}
      className={`switch-branch-dropdown-wrapper ${className} ${isDisabled ? 'switch-branch-dropdown-disabled' : ''}`}
      onKeyDown={(e) => handleKeyDown(e)}
    >
      <div className={'display-input-wrapper' + (hasError ? ' error' : '')} onClick={() => setIsSelectMenuOpen(!isSelectMenuOpen)}>
        <div className={'text-wrapper' + (!selectedKey ? ' placeholder' : '')}>
          {options?.find((o) => o.key?.toString()?.toLowerCase() === selectedKey?.toString()?.toLowerCase())?.value || t('your_branch')}
        </div>

        <CaretDownIcon className={'caret-dropdown-icon' + (isSelectMenuOpen ? ' icon-dropdown-open' : '')} />
      </div>
      {isSelectMenuOpen && (
        <div className="options-wrapper">
          <div ref={filteringRef} className="options-filter">
            <Search
              className="search"
              onSearch={(e) => searchOption(e)}
              placeholder={t('search')}
              onCancel={() => {
                searchOption(null)
                ref.current.focus()
              }}
            />
          </div>

          {filteredOptions.map((option, index) => (
            <div
              ref={(elem) => (optionsRefs.current[index] = elem)}
              key={option.key}
              className={
                'option' +
                (option.key?.toString()?.toLowerCase() === selectedKey?.toString()?.toLowerCase() ? ' selected-option' : '') +
                (option.isDisabled ? ' disabled-option' : '') +
                (keyboardFocused && focusedOption === index ? ' focused' : '')
              }
              onClick={() => handleOptionSelection(option)}
            >
              {option.value}
              {options?.find((o) => o.key === option.key)?.additionalContent}
            </div>
          ))}
          {filteredOptions.length === 0 && <div className="no-options">{t('organization_context.no-branches-found')}</div>}
        </div>
      )}
    </FocusableContainer>
  )
}

export default SwitchBranchDropdown

